import { type KeyboardEvent, memo, type ReactNode, useCallback } from "react";
import { styled } from "tamagui";
import { X as XIcon } from "@tamagui/lucide-icons";
import type { Status } from "../shared-types/status";
import { Icon } from "./Icon";
import { useInteractiveElement } from "./interactive/useInteractiveElement";
import { motionTransition, type Press } from "./Motion/motion";
import { pillTextTruncation, StyledPill, statusColorTokens } from "./StyledPill";
import { Interface1 } from "./Typography";
const ChipText = styled(Interface1, pillTextTruncation);

// One size: 40px (`$si5`) so the whole-chip remove target is tappable — just
// under the `$si5.5` 44px touch-target token, well above the 24px floor.
const CHIP_HEIGHT = "$si5";
const CHIP_SIDE_PADDING = "$sp2";
// Optical padding: the icon side (leading slot / the ✕) is tightened vs the text side.
const CHIP_ICON_SIDE_PADDING = "$sp1";
export type ChipProps = {
  /** Visible label; also names the remove control (`aria-label="Remove {label}"`). */
  label: string;
  /** Whole-chip press / Enter / Space / Backspace / Delete fire this. Omit for a
   *  static (non-interactive) chip. */
  onRemove?: () => void;
  /** Optional content before the label — colour dot, avatar, or icon. */
  leading?: ReactNode;
  status?: Status;
  disabled?: boolean;
  /** Press feedback when removable. @default "strong" */
  press?: Press;
  testID?: string;
};

// Memoized: a multi field re-renders the whole chip row on every keystroke, but
// a chip only changes when its own props do. Bites only if the parent hands
// stable props — `ChipGroup` keeps `onRemove` stable per chip for exactly this.
export const Chip = memo(function Chip({
  label,
  onRemove,
  leading,
  status = "neutral",
  disabled = false,
  press = "strong",
  testID
}: ChipProps) {
  const tokens = statusColorTokens[status];
  const {
    interactiveProps,
    renderTag
  } = useInteractiveElement({
    kind: onRemove ? "button" : "static",
    onPress: onRemove,
    disabled
  });

  // useInteractiveElement covers click + Enter/Space; chips also remove on
  // Backspace/Delete. Compose so its onKeyDown still handles the rest.
  const baseOnKeyDown = interactiveProps.onKeyDown;
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      if (!disabled) {
        onRemove?.();
      }
      return;
    }
    baseOnKeyDown?.(event);
  }, [baseOnKeyDown, disabled, onRemove]);
  const isInteractive = onRemove !== undefined;
  const resolvedPress = isInteractive && !disabled ? press : "none";
  return <StyledPill
  // Re-key on render-tag swaps so the animated ref remounts.
  key={renderTag ?? "static"} interactive={isInteractive} press={resolvedPress} {...motionTransition(isInteractive ? press : "none")} {...isInteractive ? interactiveProps : {}} onKeyDown={isInteractive ? handleKeyDown : undefined} render={renderTag ?? "span"} aria-label={isInteractive ? `Remove ${label}` : undefined} status={status} height={CHIP_HEIGHT} paddingHorizontal={CHIP_SIDE_PADDING}
  // `!= null`: leading is a ReactNode, so null and undefined both mean
  // "no leading slot" — no typed narrow distinguishes them.
  {...leading != null ? {
    paddingLeft: CHIP_ICON_SIDE_PADDING
  } : {}} {...isInteractive ? {
    paddingRight: CHIP_ICON_SIDE_PADDING
  } : {}} disabled={disabled} testID={testID}>
      {leading}
      {/* The frame's disabled variant only dims the background — a styled frame
          can't color its children, so the muted text/icon swap lives here. */}
      <ChipText color={disabled ? "$textMuted" : tokens.text} flexShrink={1} numberOfLines={1}>
        {label}
      </ChipText>
      {isInteractive ? <Icon icon={XIcon} size="$iconMedium" color={disabled ? "$iconSubtle" : tokens.icon} /> : null}
    </StyledPill>;
});